
import React from 'react';
import { Trophy, Lock, Star, Pencil, Trash2, CheckSquare, ChevronUp, ChevronDown, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export interface AchievementData {
  id: string;
  title: string;
  description: string;
  icon?: string;
  requiredStars: number;
  unlocked: boolean;
  date?: string;
  linkedTaskId?: string;
  requiredCompletions?: number;
  secret?: boolean;
  order?: number;
}

interface AchievementProps {
  achievement: AchievementData;
  totalStars: number;
  editMode?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  tasks?: Array<{ id: string; title: string; completionCount?: number }>;
}

const Achievement: React.FC<AchievementProps> = ({ 
  achievement, 
  totalStars, 
  editMode = false,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  tasks = []
}) => {
  const isUnlockable = achievement.linkedTaskId
    ? false // For task-based achievements, we don't show them as unlockable based on stars
    : totalStars >= achievement.requiredStars;
  
  const linkedTask = achievement.linkedTaskId 
    ? tasks.find(task => task.id === achievement.linkedTaskId) 
    : null;
  
  const taskProgress = linkedTask && linkedTask.completionCount !== undefined && achievement.requiredCompletions
    ? `${linkedTask.completionCount}/${achievement.requiredCompletions}`
    : null;
  
  const isTaskBased = !!achievement.linkedTaskId && !!achievement.requiredCompletions;
  
  return (
    <Card 
      className={cn(
        "relative transition-all duration-200 hover:shadow-md",
        achievement.unlocked 
          ? "border-primary/50" 
          : isUnlockable 
            ? "border-yellow-500/50 dark:border-yellow-400/50" 
            : "opacity-70"
      )}
    >
      <CardContent className="p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 flex-1">
            <div 
              className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center",
                achievement.unlocked 
                  ? "bg-primary/20 text-primary" 
                  : isUnlockable 
                    ? "bg-yellow-500/20 text-yellow-500 dark:bg-yellow-400/20 dark:text-yellow-400"
                    : "bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500"
              )}
            >
              {achievement.unlocked ? (
                <Trophy size={24} className={achievement.unlocked ? "fill-primary/20" : ""} />
              ) : achievement.secret && !editMode ? (
                <EyeOff size={24} className="opacity-70" />
              ) : (
                <div className="relative">
                  <Trophy size={24} className="opacity-70" />
                  {!isUnlockable && !isTaskBased && (
                    <div className="absolute -bottom-1 -right-1 bg-gray-500 dark:bg-gray-600 rounded-full p-0.5">
                      <Lock size={12} />
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex-1 flex flex-col justify-center">
              <h3 className={cn(
                "font-medium text-lg",
                achievement.unlocked ? "" : "text-muted-foreground"
              )}>
                {achievement.title}
              </h3>
              <p className="text-sm text-muted-foreground">{achievement.description}</p>
              
              {achievement.unlocked && achievement.date && (
                <p className="text-xs text-muted-foreground mt-1">
                  Unlocked on {new Date(achievement.date).toLocaleDateString()}
                </p>
              )}
              
              {!achievement.unlocked && linkedTask && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center">
                  <CheckSquare size={12} className="mr-1" />
                  {`Complete "${linkedTask.title}" ${achievement.requiredCompletions} times`}
                  {taskProgress && (
                    <span className="ml-1 font-medium">{taskProgress}</span>
                  )}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {!isTaskBased && !(achievement.secret && !editMode) && (
              <div className="flex items-center">
                <Star size={28} className="text-yellow-400 fill-yellow-400" />
                <span className="ml-1 font-medium text-lg">{achievement.requiredStars}</span>
              </div>
            )}
            
            {isTaskBased && taskProgress && (
              <div className="flex items-center">
                <CheckSquare size={24} />
                <span className="ml-1 font-medium text-lg">{taskProgress}</span>
              </div>
            )}
          </div>
        </div>
        
        {editMode && (
          <div className="flex justify-between mt-3">
            <div className="flex space-x-2">
              {onMoveUp && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6" 
                  onClick={onMoveUp}
                >
                  <ChevronUp size={14} />
                </Button>
              )}
              {onMoveDown && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6" 
                  onClick={onMoveDown}
                >
                  <ChevronDown size={14} />
                </Button>
              )}
            </div>
            
            <div className="flex space-x-2">
              {onEdit && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6" 
                  onClick={onEdit}
                >
                  <Pencil size={14} />
                </Button>
              )}
              {onDelete && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 text-red-500" 
                  onClick={onDelete}
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Achievement;
